import { Box, Checkbox, FormControlLabel, makeStyles, Typography } from "@material-ui/core";
import { FC, useState, useEffect, memo } from "react";
import dayjs from "dayjs";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { clone } from "ramda";

import { GenericFunction } from "../../../types/generic.types";
import { areEqual } from "../../../utils/generic";
import CustomCard from "../../../components/CustomCard";

const daysIndexes = ["0", "1", "2", "3", "4", "5", "6"];
const daysNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const useStyles = makeStyles(() => ({
  date: {
    width: "75%",
    color: "#0000008a",
    border: "1px solid #cccccc5c",
    padding: "8px 5px",
    borderRadius: "5px",
    backgroundColor: "#f6f6f6",
  },
  flexCheckBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

const Aavailabilities: FC<AavailabilitiesProps> = ({ setError, onChangeDates, datesRange }) => {
  const [dates, setDates] = useState<IWeekDate>({
    "0": [new Date(), new Date()],
    "1": [new Date(), new Date()],
    "2": [new Date(), new Date()],
    "3": [new Date(), new Date()],
    "4": [new Date(), new Date()],
    "5": [new Date(), new Date()],
    "6": [new Date(), new Date()],
  });
  const [checked, setChecked] = useState<number[]>([]);

  const handleToggleCheckBox = (value: number, day?: string, emptyArray?: boolean) => {
    let newDates = clone(dates);
    if (emptyArray && day) {
      let dayIndex = daysIndexes.indexOf(day);
      setChecked((prev) => [...prev, dayIndex]);
    } else {
      const currentIndex = checked.indexOf(value);
      const newChecked = [...checked];
      let selectedDay = daysIndexes[value];
      if (currentIndex === -1) {
        (newDates as any)[selectedDay] = [];
        newChecked.push(value);
      } else {
        (newDates as any)[selectedDay] = [new Date(), new Date()];
        newChecked.splice(currentIndex, 1);
      }
      setChecked(newChecked);
    }

    setDates(newDates);
  };

  const selectDate = (date: any, day: string, isFrom: boolean) => {
    if (date) {
      setDates((prevState: any) => ({
        ...prevState,
        [day]: [isFrom ? date : prevState[day][0], !isFrom ? date : prevState[day][1]],
      }));

      checkObject();
    }
  };

  const checkObject = () => {
    for (const [_, value] of Object.entries(dates)) {
      let date = `${value}`.split(",", 2);
      if (date[0] !== date[1] && value.length) {
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    createAavailabilities();
    setError(checkObject());
  }, [dates]);

  useEffect(() => {
    if (datesRange) {
      const result = Object.entries(datesRange)
        .map((entry, index) => {
          if (!entry[1].length) {
            handleToggleCheckBox(index, entry[0], true);
          }
          return {
            [entry[0]]: entry[1].length
              ? [
                  dayjs().startOf("day").add(entry[1][0], "seconds").toDate(),
                  dayjs().startOf("day").add(entry[1][1], "seconds").toDate(),
                ]
              : [],
          };
        })
        .reduce((acc, range) => {
          const key = Object.keys(range)[0];
          return {
            ...acc,
            [key]: range[key],
          };
        }, {});
      setDates(result as any);
    }
  }, []);

  const createAavailabilities = () => {
    let newDates = clone(dates);

    for (const value of Object.values(newDates)) {
      if (value.length) {
        (value as any)[0] = value[0].getHours() * 60 * 60 + value[0].getMinutes() * 60;
        (value as any)[1] = value[1].getHours() * 60 * 60 + value[1].getMinutes() * 60;
      }
    }

    onChangeDates(newDates);
  };

  const classes = useStyles({});
  return (
    <Box display="flex" flexWrap="wrap" justifyContent="space-between">
      {daysIndexes.map((day, index) =>
        (dates as any)[day] ? (
          <CustomCard width="25%" margin="0 5px" padding="16px 8px" key={index}>
            <Box className={classes.flexCheckBox}>
              <Typography variant="h6" color="textSecondary">
                {daysNames[index]}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    key={index}
                    onClick={() => handleToggleCheckBox(index)}
                    checked={checked.indexOf(index) !== -1}
                    tabIndex={-1}
                    disableRipple
                  />
                }
                label="Day off"
              />
            </Box>
            <Box display="flex" mt={2}>
              <Box mt={1}>
                <Typography variant="body2" color="textSecondary">
                  From
                </Typography>
                <DatePicker
                  className={classes.date}
                  selected={(dates as any)[day][0]}
                  onChange={(date) => selectDate(date, day, true)}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={30}
                  timeCaption="Time"
                  dateFormat="h:mm aa"
                  disabled={(dates as any)[day].length ? checked.indexOf(index) !== -1 : true}
                  minTime={new Date(0, 0, 0, 0, 0)}
                  maxTime={
                    (dates as any)[day].length
                      ? checked.indexOf(index) === -1
                        ? new Date(0, 0, 0, checkObject() ? 23 : (dates as any)[day][1].getHours() - 1, 59)
                        : new Date(0, 0, 0, 0)
                      : new Date(0, 0, 0, 0)
                  }
                />
              </Box>
              <Box mt={1}>
                <Typography variant="body2" color="textSecondary">
                  To
                </Typography>
                <DatePicker
                  className={classes.date}
                  selected={(dates as any)[day][1]}
                  onChange={(date) => selectDate(date, day, false)}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={30}
                  timeCaption="Time"
                  dateFormat="h:mm aa"
                  disabled={(dates as any)[day].length ? checked.indexOf(index) !== -1 : true}
                  minTime={
                    (dates as any)[day].length
                      ? checked.indexOf(index) === -1
                        ? new Date(0, 0, 0, (dates as any)[day][0].getHours() + 1, 0)
                        : new Date(0, 0, 0, 0)
                      : new Date(0, 0, 0, 0)
                  }
                  maxTime={new Date(0, 0, 0, 23, 59)}
                />
              </Box>
            </Box>
          </CustomCard>
        ) : null
      )}
    </Box>
  );
};

interface IWeekDate {
  "0": [Date, Date];
  "1": [Date, Date];
  "2": [Date, Date];
  "3": [Date, Date];
  "4": [Date, Date];
  "5": [Date, Date];
  "6": [Date, Date];
}

interface AavailabilitiesProps {
  setError: GenericFunction;
  onChangeDates: GenericFunction;
  datesRange?: IWeekDate;
}

export default memo(Aavailabilities, areEqual);
